import {
  CheckboxElement,
  TextFieldElement,
  useFormContext,
} from "react-hook-form-mui"
import { useWatch } from "react-hook-form"
import { Stack, Typography } from "@mui/material"
import UrlButtons from "@/modules/postBot/components/posts/postField/urlButtons"
import { IMailingFormData } from "./mailingForm"
import { useEffect, useRef } from "react"
import { TgMailing } from "../../../interfaces"
import { getMediaFieldNameByMethod } from "./utils"
import { ParseModeSelectField } from "../parseModeSelectField"
import { Editor } from "./quill/editor"
import { deltaToMessageEntities } from "./quill/deltaToMessageEntities"
import Quill from "quill"
import {
  validateChatIdLike,
  validateTextLength,
  validateMediaUrl,
  validateMessageIdsField,
  validateHtmlMarkup,
  validateMarkdownV2Markup,
  validateMarkdownMarkup,
  TEXT_MAX_LENGTH,
  CAPTION_MAX_LENGTH,
} from "../../../constants/mailing"

interface IProps {}
interface IFormData<M extends TgMailing.AvailableMethods> {
  method: M
  body: IMailingFormData<M>["body"]
}

/** Inline character counter shown as helperText under text / caption fields. */
const CharCounter = ({ name, max }: { name: string; max: number }) => {
  const val = useWatch({ name }) ?? ""
  const len = String(val).length
  return (
    <Typography
      component="span"
      variant="caption"
      color={len > max ? "error.main" : "text.secondary"}
    >
      {len} / {max}
    </Typography>
  )
}

/** Maps parse_mode → the matching markup validator (or undefined for no-markup). */
const getMarkupValidator = (parseMode: string | undefined) => {
  if (parseMode === "HTML") return validateHtmlMarkup
  if (parseMode === "MarkdownV2") return validateMarkdownV2Markup
  if (parseMode === "Markdown") return validateMarkdownMarkup
  return undefined
}

/** Returns a combined validate function: length check then markup check. */
const makeTextValidate =
  (max: number, parseMode: string | undefined) =>
  (v: unknown): true | string => {
    const lenResult = validateTextLength(max)(v)
    if (lenResult !== true) return lenResult
    return getMarkupValidator(parseMode)?.(v) ?? true
  }

/**
 * Дефолтные значения тела сообщения по выбранному методу. Для MTProto-методов
 * (forwardMessages / copyMessages) — пустые from_chat_id и message_ids;
 * это «сырые» значения формы (до сабмита) — поэтому используется any-cast:
 * нормализация в number | `@${string}` / number[] выполняется в
 * mailingForm.tsx → normalizeBody / getBodyValues.
 */
const DEFAULT_VALUES: {
  [M in TgMailing.AvailableMethods]: Omit<TgMailing.ApiMethodBody[M], "chat_id">
} = {
  sendMessage: {
    message_thread_id: undefined,
    text: "",
    parse_mode: undefined,
    entities: undefined,
    disable_web_page_preview: undefined,
    disable_notification: undefined,
    protect_content: undefined,
    reply_to_message_id: undefined,
    allow_sending_without_reply: undefined,
    reply_markup: undefined,
  },
  sendChatAction: {
    action: "typing",
    message_thread_id: undefined,
  },
  sendAnimation: {
    animation: undefined as any,
    caption: "",
  },
  sendPhoto: {
    photo: undefined as any,
    caption: "",
  },
  sendVideo: {
    video: undefined as any,
    caption: "",
  },
  forwardMessages: {
    from_chat_id: "" as unknown as TgMailing.ChatIdLike,
    message_ids: "" as unknown as number[],
    disable_notification: undefined,
    protect_content: undefined,
    drop_author: undefined,
  },
  copyMessages: {
    from_chat_id: "" as unknown as TgMailing.ChatIdLike,
    message_ids: "" as unknown as number[],
    disable_notification: undefined,
    protect_content: undefined,
  },
}

export const MailingBody = <
  M extends TgMailing.AvailableMethods,
  T extends IFormData<M>,
>(
  props: IProps,
) => {
  const formContext = useFormContext<IFormData<TgMailing.AvailableMethods>>()
  const method = formContext.watch("method")
  const parseMode = formContext.watch("body.parse_mode" as any)
  const quillRef = useRef<Quill>()

  const { register, unregister } = formContext

  // isMedia must be declared before the effects that use it
  const isMedia =
    method === "sendPhoto" ||
    method === "sendVideo" ||
    method === "sendAnimation"

  // Register body.text with length validation when Quill is active (sendMessage, no parseMode)
  useEffect(() => {
    if (method === "sendMessage" && !parseMode) {
      register("body.text" as any, {
        validate: validateTextLength(TEXT_MAX_LENGTH),
      })
      return () => unregister("body.text" as any, { keepValue: true })
    }
  }, [method, parseMode])

  // Register body.caption with length validation when Quill is active (media methods, no parseMode)
  useEffect(() => {
    if (isMedia && !parseMode) {
      register("body.caption" as any, {
        validate: validateTextLength(CAPTION_MAX_LENGTH),
      })
      return () => unregister("body.caption" as any, { keepValue: true })
    }
  }, [method, parseMode])

  useEffect(() => {
    if (!method) return
    formContext.setValue("body", DEFAULT_VALUES[method])
  }, [method])

  const isMtproto = TgMailing.isMtprotoMethod(method)

  return (
    <Stack spacing={1}>
      {/* MTProto-ветка: пересылка/копирование пачки сообщений */}
      {isMtproto && (
        <>
          <TextFieldElement
            label="Источник (chat_id или @username)"
            name="body.from_chat_id"
            required
            fullWidth
            helperText="Числовой ID канала/чата (например, -100…) или @username публичного канала"
            validation={{
              validate: (v: unknown) => validateChatIdLike(v),
            }}
          />
          <TextFieldElement
            label="ID сообщений"
            name="body.message_ids"
            required
            fullWidth
            helperText="Числа через запятую или пробел: 12, 13, 14"
            validation={{ validate: validateMessageIdsField }}
          />
          <CheckboxElement
            name="body.disable_notification"
            label="Отключить уведомление"
          />
          <CheckboxElement
            name="body.protect_content"
            label="Защитить контент (запретить пересылать/делать скриншоты)"
          />
          {method === "forwardMessages" && (
            <CheckboxElement
              name="body.drop_author"
              label="Скрыть автора (без подписи «Forwarded from …»)"
            />
          )}
        </>
      )}

      {/* Bot API-ветка */}
      {(method === "sendMessage" ||
        method === "sendPhoto" ||
        method === "sendVideo" ||
        method === "sendAnimation") && (
        <ParseModeSelectField<IMailingFormData> />
      )}
      {method === "sendMessage" &&
        (!!parseMode ? (
          <TextFieldElement
            label="Текст сообщения"
            name="body.text"
            required
            multiline
            rows={14}
            fullWidth
            sx={{ textarea: { resize: "both" } }}
            validation={{
              validate: makeTextValidate(TEXT_MAX_LENGTH, parseMode),
            }}
            helperText={<CharCounter name="body.text" max={TEXT_MAX_LENGTH} />}
          />
        ) : (
          <Editor
            ref={quillRef as any}
            onTextChange={() => {
              const { entities, text } = deltaToMessageEntities(quillRef)

              formContext.setValue("body.text" as any, text, {
                shouldValidate: true,
              })
              formContext.setValue("body.entities" as any, entities)
            }}
          />
        ))}
      {/* CharCounter below Quill for sendMessage */}
      {method === "sendMessage" && !parseMode && (
        <CharCounter name="body.text" max={TEXT_MAX_LENGTH} />
      )}
      {(method === "sendPhoto" ||
        method === "sendVideo" ||
        method === "sendAnimation") && (
        <>
          {!!parseMode ? (
            <TextFieldElement
              label="Подпись"
              name="body.caption"
              multiline
              rows={14}
              fullWidth
              sx={{ textarea: { resize: "both" } }}
              validation={{
                validate: makeTextValidate(CAPTION_MAX_LENGTH, parseMode),
              }}
              helperText={
                <CharCounter name="body.caption" max={CAPTION_MAX_LENGTH} />
              }
            />
          ) : (
            <Editor
              ref={quillRef as any}
              onTextChange={() => {
                const { entities, text } = deltaToMessageEntities(quillRef)

                formContext.setValue("body.caption" as any, text, {
                  shouldValidate: true,
                })
                formContext.setValue("body.caption_entities" as any, entities)
              }}
            />
          )}
          {/* CharCounter below Quill for media methods */}
          {!parseMode && (
            <CharCounter name="body.caption" max={CAPTION_MAX_LENGTH} />
          )}
          <TextFieldElement
            label="Ссылка на файл"
            name={`body.${getMediaFieldNameByMethod(method)}`}
            fullWidth
            required
            validation={{ validate: validateMediaUrl }}
          />
        </>
      )}
      {(method === "sendMessage" ||
        method === "sendPhoto" ||
        method === "sendVideo" ||
        method === "sendAnimation") && (
        <UrlButtons
          data={formContext.watch("body.reply_markup" as any)}
          onChange={(val) =>
            formContext.setValue("body.reply_markup" as any, val)
          }
        />
      )}
    </Stack>
  )
}
